import { CheckOutlined, InfoCircleFilled } from '@ant-design/icons'
import {
  Button,
  EmptyState,
  NoDataIcon,
  NotStartedIcon,
  Table
} from '@lmc/ui-kit'
import type { SorterResult } from 'antd/es/table/interface'
import { Spinner } from 'components/generic'
import { SETTINGS } from 'components/routes/settings'
import dayjs from 'dayjs'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useGetInvoicingSettings } from 'screens/Rostering/hooks/useInvoicingRates'
import styled from 'styled-components'
import {
  useBulkUpdateChargeBy,
  useGetChargeReconciliation,
  useUpdateChargeReconciliation
} from '../../../Reports/hooks/useChargeReconciliation'
import {
  ChargeReconciliationChargeBy,
  type ChargeReconciliationRow
} from '../../../Reports/types/chargeReconciliation'
import type { ChargeReportToReconciliationNavigationState } from '../../../Reports/types/chargeReport'
import { RotaDrawer } from '../../RotaDrawer/RotaDrawer'
import {
  CHARGE_RECONCILIATION_PAGE_SIZE,
  ChargeRatesFiltersBar,
  useChargeRatesFilters
} from './ChargeRatesFilters'
import { CustomChargeAmountModal, DontChargeModal } from './ChargeRatesModals'
import { getChargeRatesTableColumns } from './chargeRatesTableColumns'
import { rowKey } from './chargeRatesUtils'

const ChargeRatesCustomCellWrapper = ({
  children,
  ...props
}: {
  children: ReactNode
}) => <ChargeRatesCustomCell {...props}>{children}</ChargeRatesCustomCell>

const ChargeRatesCustomTableWrapper = ({
  children,
  ...props
}: {
  children: ReactNode
}) => <ChargeRatesCustomTable {...props}>{children}</ChargeRatesCustomTable>

const chargeRatesTableComponents = {
  table: ChargeRatesCustomTableWrapper,
  body: {
    cell: ChargeRatesCustomCellWrapper
  }
}

export const ChargeRatesReconciliationTab = ({
  nominative
}: {
  nominative: string
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const sendEvent = useMixpanelEvent()

  // Charge rates are enabled per-home via the invoicing settings toggle
  // (Finance settings page). When off, the tab shows the "turned off" state.
  const { data: invoicingSettings, isLoading: isEnabledLoading } =
    useGetInvoicingSettings()
  const chargeRatesEnabled = invoicingSettings?.enabled ?? false

  const filterState = useChargeRatesFilters(nominative)
  const {
    filters,
    page,
    setPage,
    setSortBy,
    setSortOrder,
    selectedRowKeys,
    setSelectedRowKeys,
    setDateRange,
    setStatusFilter,
    setServiceUserFilter,
    setFundingTypeFilter
  } = filterState

  // Deep-link from the Charge Report "X events" tag: preset the filters to the
  // funder/service-user's not-yet-reconciled events, then clear the nav state
  // so it doesn't re-fire on subsequent renders.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run when the incoming navigation state changes
  useEffect(() => {
    const st =
      location.state as ChargeReportToReconciliationNavigationState | null
    if (!st?.fromChargeReport) {
      return
    }
    if (st.serviceUserIds?.length) {
      setServiceUserFilter(st.serviceUserIds)
    }
    if (st.fundingTypeIds?.length) {
      setFundingTypeFilter(st.fundingTypeIds)
    }
    if (st.status) {
      setStatusFilter(st.status)
    }
    if (st.startTime && st.endTime) {
      setDateRange([dayjs(st.startTime), dayjs(st.endTime)])
    }
    setPage(1)
    setSelectedRowKeys([])
    navigate(location.pathname, { replace: true, state: undefined })
  }, [location.pathname, location.state, navigate])

  const bulkChargeTypeOptions = useMemo(
    () => [
      {
        label: t('RECONCILIATION_CHARGE_BY_PLANNED'),
        value: ChargeReconciliationChargeBy.PLANNED_TIME
      }
    ],
    [t]
  )

  const [drawerInstanceId, setDrawerInstanceId] = useState<string | null>(null)
  const [dontChargeRow, setDontChargeRow] =
    useState<ChargeReconciliationRow | null>(null)
  const [customChargeRow, setCustomChargeRow] =
    useState<ChargeReconciliationRow | null>(null)

  // Don't fetch reconciliation data until we know invoicing is enabled for the
  // home — avoids a wasted charge request (and fetching charge data) when off.
  const { data, isLoading, isFetching } = useGetChargeReconciliation(
    filters,
    chargeRatesEnabled
  )
  const rows = data?.data ?? []
  const total = data?.pagination?.totalItems ?? 0

  const { mutate: bulkUpdateChargeBy, isLoading: isBulkReconciling } =
    useBulkUpdateChargeBy(() => setSelectedRowKeys([]))
  const { mutate: updateDontCharge, isLoading: isDontChargeLoading } =
    useUpdateChargeReconciliation(() => setDontChargeRow(null))
  const { mutate: updateCustomCharge, isLoading: isCustomChargeLoading } =
    useUpdateChargeReconciliation(() => setCustomChargeRow(null))

  const handleTableChange = (
    _pagination: unknown,
    _filters: unknown,
    sorter:
      | SorterResult<ChargeReconciliationRow>
      | SorterResult<ChargeReconciliationRow>[],
    extra: { action: 'paginate' | 'sort' | 'filter' }
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter
    if (s?.columnKey && s.order) {
      setSortBy(String(s.columnKey))
      setSortOrder(s.order === 'ascend' ? 'asc' : 'desc')
    } else {
      setSortBy(undefined)
      setSortOrder(undefined)
    }
    if (extra?.action !== 'paginate') {
      setPage(1)
      setSelectedRowKeys([])
    }
  }

  const handleBulkReconcile = () => {
    // Cancelled events are chargeable on the charge tab (providers may charge a
    // cancelled visit), so they're included in bulk — unlike the pay tab.
    const rotaInstanceIds = rows
      .filter((r) => selectedRowKeys.includes(rowKey(r)))
      .map((r) => r.rotaInstanceId)
    if (rotaInstanceIds.length === 0) {
      return
    }
    const bulkEventProps = {
      numberOfEventsSelected: selectedRowKeys.length,
      filtersApplied: {
        status: 'status' in filters,
        fundingType: 'fundingTypeIds' in filters,
        serviceUser: 'serviceUserIds' in filters,
        eventType: 'eventTypeIds' in filters,
        eventStatus: 'eventStatuses' in filters,
        warnings: 'warnings' in filters
      }
    }
    bulkUpdateChargeBy(
      {
        rotaInstanceIds,
        chargeBy: ChargeReconciliationChargeBy.PLANNED_TIME
      },
      {
        onSuccess: () =>
          sendEvent(MixpanelEvents.CHARGE_RECONCILIATION_BULK_ACTION, {
            ...bulkEventProps,
            errorShown: false
          }),
        onError: () =>
          sendEvent(MixpanelEvents.CHARGE_RECONCILIATION_BULK_ACTION, {
            ...bulkEventProps,
            errorShown: true
          })
      }
    )
  }

  const handleDontChargeClick = useCallback(
    (record: ChargeReconciliationRow) => {
      setDontChargeRow(record)
    },
    []
  )

  const handleEditChargeAmount = useCallback(
    (record: ChargeReconciliationRow) => {
      setCustomChargeRow(record)
    },
    []
  )

  const columns = useMemo(
    () =>
      getChargeRatesTableColumns({
        t,
        nominative,
        onDontCharge: handleDontChargeClick,
        onEditChargeAmount: handleEditChargeAmount
      }),
    [t, nominative, handleDontChargeClick, handleEditChargeAmount]
  )

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => {
      setSelectedRowKeys(keys as string[])
    },
    columnTitle: (checkboxNode: ReactNode) => (
      <span data-testid="reconciliation-charge-select-all">{checkboxNode}</span>
    ),
    renderCell: (
      _checked: boolean,
      _record: ChargeReconciliationRow,
      _index: number,
      originNode: ReactNode
    ) => (
      <span data-testid="reconciliation-charge-row-checkbox">{originNode}</span>
    )
  }

  const pagination = {
    current: page,
    pageSize: CHARGE_RECONCILIATION_PAGE_SIZE,
    total,
    onChange: (p: number) => {
      setPage(p)
      setSelectedRowKeys([])
    },
    showSizeChanger: false,
    position: ['bottomCenter'] as 'bottomCenter'[]
  }

  const hasRows = rows.length > 0
  const hasSelection = selectedRowKeys.length > 0
  const showTableLoader = (isLoading || isFetching) && !hasRows
  const showSelectHint = !hasSelection && !isLoading && hasRows
  const showEmpty = !isLoading && !isFetching && !hasRows

  const onRow = (record: ChargeReconciliationRow) => ({
    'data-testid': 'reconciliation-charge-table-row',
    onClick: (e: React.MouseEvent<HTMLElement>) => {
      const target = e.target as HTMLElement
      if (target.closest('.ant-table-selection-column')) {
        return
      }
      setDrawerInstanceId(record.rotaInstanceId)
    },
    style: { cursor: 'pointer' }
  })

  if (isEnabledLoading) {
    return (
      <TableLoaderWrap>
        <Spinner />
      </TableLoaderWrap>
    )
  }

  if (!chargeRatesEnabled) {
    return (
      <TurnedOffWrap>
        <EmptyState
          icon={<NotStartedIcon />}
          title={t('RECONCILIATION_CHARGE_TURNED_OFF_TITLE')}
          description={t('RECONCILIATION_CHARGE_TURNED_OFF_DESCRIPTION')}
        >
          <Button
            variant="primary"
            onClick={() => navigate(`/${SETTINGS.FINANCE}`)}
          >
            {t('RECONCILIATION_CHARGE_TURNED_OFF_CTA')} &rarr;
          </Button>
        </EmptyState>
      </TurnedOffWrap>
    )
  }

  return (
    <TabBody>
      <ChargeRatesFiltersBar {...filterState} />

      {showSelectHint ? (
        <BulkInfoBar>
          <BulkInfoIcon />
          <BulkInfoText>{t('RECONCILIATION_SELECT_ROWS_INFO')}</BulkInfoText>
        </BulkInfoBar>
      ) : hasSelection ? (
        <BulkActionBar>
          <BulkSelectionCount>
            {t('RECONCILIATION_N_SELECTED', { count: selectedRowKeys.length })}
          </BulkSelectionCount>
          <BulkToolbarDivider />
          <BulkRadioInner>
            {bulkChargeTypeOptions.map((opt) => (
              <BulkRadioLabel key={opt.value}>
                <BulkRadioInput
                  type="radio"
                  name="bulk-charge-type"
                  value={opt.value}
                  checked
                  readOnly
                />
                {opt.label}
              </BulkRadioLabel>
            ))}
          </BulkRadioInner>
          <BulkReconcileWrap>
            <Button
              variant="primary"
              onClick={handleBulkReconcile}
              isLoading={isBulkReconciling}
              icon={<CheckOutlined />}
            >
              {t('RECONCILIATION_RECONCILE')}
            </Button>
          </BulkReconcileWrap>
        </BulkActionBar>
      ) : null}

      {showTableLoader ? (
        <TableLoaderWrap>
          <Spinner />
        </TableLoaderWrap>
      ) : showEmpty ? (
        <EmptyStateWrap>
          <EmptyState
            icon={<NoDataIcon />}
            title={t('RECONCILIATION_EMPTY_TITLE')}
            description={t('RECONCILIATION_EMPTY_DESCRIPTION')}
          />
        </EmptyStateWrap>
      ) : (
        <ChargeRatesTableScroll>
          <StyledTable
            components={chargeRatesTableComponents}
            columns={columns}
            dataSource={rows}
            rowKey={rowKey}
            loading={isFetching}
            rowClassName={(_, index) =>
              index % 2 === 0 ? 'even-row' : 'odd-row'
            }
            onRow={onRow}
            rowSelection={rowSelection}
            pagination={pagination}
            onChange={handleTableChange}
            size="small"
            scroll={{ x: 'max-content' }}
          />
        </ChargeRatesTableScroll>
      )}

      {drawerInstanceId && (
        <RotaDrawer
          data-testid="reconciliation-charge-event-drawer"
          isVisible={true}
          id={drawerInstanceId}
          onClose={() => setDrawerInstanceId(null)}
        />
      )}

      <DontChargeModal
        open={dontChargeRow !== null}
        row={dontChargeRow}
        onClose={() => setDontChargeRow(null)}
        isLoading={isDontChargeLoading}
        onSave={(row, reason) => {
          sendEvent(MixpanelEvents.CHARGE_RECONCILIATION_DONT_CHARGE, {
            serviceUserId: row.serviceUser?.id ?? null,
            eventName: row.eventType.name,
            eventTypeId: row.eventType.id,
            startTime: row.plannedTime.startTime,
            isCanceled: row.eventStatus === 'CANCELLED',
            isMissed: row.eventStatus === 'MISSED',
            reason
          })
          updateDontCharge({
            rotaInstanceId: row.rotaInstanceId,
            payload: {
              chargeStatus: 'reconciled',
              chargeBy: ChargeReconciliationChargeBy.DONT_CHARGE,
              dontChargeReason: reason
            }
          })
        }}
      />

      <CustomChargeAmountModal
        open={customChargeRow !== null}
        row={customChargeRow}
        onClose={() => setCustomChargeRow(null)}
        isLoading={isCustomChargeLoading}
        onSave={(row, amount) => {
          sendEvent(MixpanelEvents.CHARGE_RECONCILIATION_CUSTOM_AMOUNT, {
            serviceUserId: row.serviceUser?.id ?? null,
            eventName: row.eventType.name,
            eventTypeId: row.eventType.id,
            startTime: row.plannedTime.startTime,
            originalChargeAmount: row.chargeAmount,
            newChargeAmount: amount
          })
          updateCustomCharge({
            rotaInstanceId: row.rotaInstanceId,
            payload: {
              chargeStatus: 'reconciled',
              chargeBy: ChargeReconciliationChargeBy.CUSTOM_AMOUNT,
              customChargeAmount: String(amount)
            }
          })
        }}
      />
    </TabBody>
  )
}

export default ChargeRatesReconciliationTab

const TabBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const TurnedOffWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  width: 100%;
`

const EmptyStateWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  width: 100%;
  border-radius: 20px;
  background: ${({ theme }) => theme.color.neutral.color10};
`

const TableLoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  width: 100%;
  border-radius: 20px;
  background: ${({ theme }) => theme.color.neutral.color10};
`

const ChargeRatesTableScroll = styled.div`
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  border-radius: 20px;
`

const StyledTable = styled(Table)`
  & .ant-table {
    min-width: max-content;
  }

  & .ant-table-thead > tr > th {
    white-space: nowrap;
  }

  & .ant-table-column-sorters {
    flex-direction: row-reverse;
    justify-content: flex-end;
  }

  & .ant-table-column-sorter {
    margin-right: 4px;
    margin-left: 0;
  }

  & .ant-table-thead > tr > th.reconciliation-header-multiline {
    white-space: pre-line;
    vertical-align: bottom;
  }

  & .even-row {
    background-color: ${({ theme }) => theme.color.neutral.color11};
  }

  & .odd-row {
    background-color: ${({ theme }) => theme.color.neutral.color10};
  }

  & .ant-table-tbody > tr.ant-table-row-selected > td {
    background: inherit;
  }
` as typeof Table

const BulkToolbarSurface = styled.div`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-height: 56px;
  padding: 10px 16px;
  background: ${({ theme }) => theme.color.neutral.color11};
  border: 1px solid ${({ theme }) => theme.color.neutral.color9};
  border-radius: 8px;
`

const BulkInfoBar = styled(BulkToolbarSurface)`
  gap: 12px;
`

const BulkInfoIcon = styled(InfoCircleFilled)`
  color: ${({ theme }) => theme.color.status.primary};
  font-size: 14px;
  flex-shrink: 0;
`

const BulkInfoText = styled.span`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-size: 14px;
  line-height: 1.4;
`

const BulkActionBar = styled(BulkToolbarSurface)`
  flex-wrap: wrap;
  gap: 16px;
`

const BulkSelectionCount = styled.span`
  color: ${({ theme }) => theme.color.neutral.color2};
  font-weight: 500;
  flex-shrink: 0;
`

const BulkToolbarDivider = styled.div`
  width: 1px;
  align-self: stretch;
  min-height: 24px;
  background: ${({ theme }) => theme.color.neutral.color8};
  flex-shrink: 0;
`

const BulkReconcileWrap = styled.div`
  margin-left: auto;
  display: flex;
`

const BulkRadioInner = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const BulkRadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`

const BulkRadioInput = styled.input`
  accent-color: #1677ff;
  cursor: pointer;
  margin: 0;
`

const ChargeRatesCustomTable = styled.table`
  background: ${({ theme }) => theme.color.background.tertiary};
`

const ChargeRatesCustomCell = styled.td`
  padding: 16px 12px !important;
  background-color: inherit !important;
`
